import React, { useMemo } from "react";
import * as PropTypes from "prop-types";
import "./PaginationController.scss";
import { ROW_PER_PAGE_OPTIONS } from "./PaginationControllerConstant";
import Select, { StylesConfig, GroupBase, SingleValue } from "react-select";
import styles from "../../constants/index";
import { PaginationOptionsType, RowPerPageSelectorProps } from "./types";

const customStyles: StylesConfig<
  PaginationOptionsType,
  false,
  GroupBase<any>
> = {
  container: (provided) => {
    return {
      ...provided,
      display: "inline-block",
    };
  },
  control: (provided, state) => {
    let borderStyles = { border: `1px solid ${styles.surface["100"]}` };

    if (state.isDisabled) {
      borderStyles = { border: `1px solid ${styles.surface["200"]}` };
    } else if (state.isFocused) {
      borderStyles = { border: `1px solid ${styles.navy["500"]}` };
    }

    return {
      ...provided,
      cursor: "pointer",
      borderRadius: "6px",
      width: "72px",
      color: state.isDisabled ? styles.text["400"] : `${styles.text["800"]}`,
      caretColor: `${styles.navy["500"]}`,
      boxShadow: "none",
      "&:hover": {
        border: `1px solid ${styles.navy["500"]}`,
      },
      ...borderStyles,
      margin: "0px 10px",
      minHeight: "25px",
    };
  },
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  placeholder: (provided) => ({ ...provided, margin: 0 }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0px 5px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: 0,
    fill: styles.surface["800"],
    stroke: styles.surface["800"],
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    padding: "0px 5px",
    transform: !state.selectProps.menuIsOpen ? "rotate(180deg)" : undefined,
    transition: "transform 0.3s",
  }),
  option: (provided, state) => ({
    ...provided,
    cursor: "pointer",
    backgroundColor: state.isSelected
      ? styles.navy["50"]
      : state.isFocused
        ? styles.surface["75"]
        : "#fff",
    ":active": {
      backgroundColor: styles.navy["50"],
    },
    padding: "8px 12px",
    color: styles.text["800"],
    position: "relative",
  }),
  menu: (provided) => ({
    ...provided,
    width: "max-content",
    maxWidth: "350px",
    marginTop: "12px",
    zIndex: styles.zIndexPopover,
  }),
};

const ROW_PER_PAGE_SELECT_OPTIONS = ROW_PER_PAGE_OPTIONS.map((option) => ({
  label: option,
  value: option,
}));

const RowPerPageSelector = (props: RowPerPageSelectorProps) => {
  const { rowPerPage } = props.paginationDetail;

  const rowPerPageSelectValue = useMemo(
    () => (rowPerPage ? { label: rowPerPage, value: rowPerPage } : undefined),
    [rowPerPage]
  );

  const onSelect = (newValue: SingleValue<PaginationOptionsType>) => {
    if (newValue) {
      props.onRowPerPageSelect(newValue.value);
    }
  };

  return (
    <div className="rowPerPageSelector">
      Items per page:
      <Select
        styles={customStyles}
        options={ROW_PER_PAGE_SELECT_OPTIONS}
        value={rowPerPageSelectValue}
        onChange={onSelect}
        menuPlacement="top"
        placeholder=""
      />
    </div>
  );
};

RowPerPageSelector.propTypes = {
  paginationDetail: PropTypes.object.isRequired,
  onRowPerPageSelect: PropTypes.func.isRequired,
};

export default RowPerPageSelector;
